import { useEffect } from 'react';
import { getAuth, signOut } from 'firebase/auth';

// ============================================================
// OWNER AUTH CONFIG — single source of truth for this project.
// For this app, a single owner identity is expected.
// Set REACT_APP_OWNER_EMAIL in environment for deployment.
// ============================================================
const DEFAULT_OWNER_EMAIL = 'rmcmanus@ramdesignworks.com';

export const INTENTIONAL_SIGNOUT_KEY = 'ea_intentional_signout';

export const ALLOWED_EMAILS = [
  (process.env.REACT_APP_OWNER_EMAIL || DEFAULT_OWNER_EMAIL).trim().toLowerCase(),
];

export function isAllowedEmail(email) {
  return ALLOWED_EMAILS.includes((email || '').toLowerCase());
}

/**
 * Auth guard hook — call once at the root of the app.
 *
 * Rules:
 *  - user === undefined  → auth state still loading; do nothing yet
 *  - user === null       → no UID (fresh visit, expired session, intentional sign-out);
 *                          do nothing — App.js renders <LoginPage /> naturally.
 *  - signed in but not owner email → sign out → /purgatory.html (authenticated intruder)
 *  - user in allowlist   → authorized; app continues
 *
 * @param {object|null|undefined} user - Firebase Auth user object, null, or undefined (loading)
 */
export function useAuthGuard(user) {
  const auth = getAuth();

  useEffect(() => {
    // Auth state is still initializing — do not redirect yet.
    if (user === undefined) return;

    // No Firebase UID — unauthenticated (fresh visit, expired session, or intentional signout).
    // Always allow App.js to render <LoginPage />.
    // Purgatory is reserved for signed-in intruders only (wrong email).
    if (!user || !user.uid) {
      sessionStorage.removeItem(INTENTIONAL_SIGNOUT_KEY);
      return;
    }

    // Signed in but email is not owner allowlist — actual intruder, boot to purgatory.
    if (!isAllowedEmail(user.email)) {
      signOut(auth).finally(() => {
        window.location.replace('/purgatory.html');
      });
    }
  }, [user, auth]);
}
